import { useState, useEffect, useRef } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import DataTable from 'react-data-table-component';
import { useSnackbar } from "notistack";
import { APIUrl } from '../../../Helper/APIUrl.js';

function Banner() {
    const Token = JSON.parse(localStorage.getItem('tcn'));
    const UserId = JSON.parse(localStorage.getItem('userid'));

    const { enqueueSnackbar } = useSnackbar();

    //Insert and Update UseStates
    const [BannerId, setBannerId] = useState("");
    const [HeroSectionId, setHeroSectionId] = useState("");
    const [Images, setImages] = useState([]);
    const [IndexNo, setIndexNo] = useState("");
    const [IsActive, setIsActive] = useState(true);
    // loading indicator for network requests
    const [loading, setLoading] = useState(false);

    //Edit Populates States
    const [edit, setEdit] = useState(false);
    const [search, setSearch] = useState("");
    const [BannerData, setBannerData] = useState([]);
    const [filteredBannerData, setFilteredBannerData] = useState([]);
    const [HeroSectionList, setHeroSectionList] = useState([]);

    //Refs for input fields
    const ImageRef = useRef();
    const HeroSectionRef = useRef(null);

    const parseJSON = async (response) => {
        try {
            const text = await response.text();
            if (!text) return null;
            return JSON.parse(text);
        }
        catch (err) {
            console.warn('Failed to parse JSON response', err);
            return null;
        }
    }

    function doEmptyStates() {
        Images.forEach((image) => {
            if (image?.preview && image.preview.startsWith('blob:')) {
                URL.revokeObjectURL(image.preview);
            }
        });
        setBannerId("");
        setHeroSectionId("");
        setImages([]);
        setIndexNo("");
        setIsActive(true);
        setEdit(false);
        if (ImageRef.current) {
            ImageRef.current.value = "";
        }
    }

    const getAllHeroSection = async () => {
        try {
            const response = await fetch(APIUrl.HeroSection + 'GetAllHeroSection',
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${Token}`
                    },
                    body: JSON.stringify({})
                });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await parseJSON(response);
            if (result && result.data != null && result.Message === "Success") {
                setHeroSectionList(result.data);
            }
            else {
                setHeroSectionList([]);
            }
        }
        catch (error) {
            console.error("Error fetching Hero Section Data:", error);
            setHeroSectionList([]);
        }
    };

    const getAllBanners = async () => {
        try {

            const response = await fetch(APIUrl.BannerSection + 'GetAllBannerSection',
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${Token}`
                    },
                    body: JSON.stringify({})
                });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await parseJSON(response);
            if (result && result.data != null && result.Message === "Success") {
                setBannerData(result.data);
                setFilteredBannerData(result.data);
            }
            else {
                setBannerData([]);
                setFilteredBannerData([]);
            }
        }
        catch (error) {
            console.error("Error fetching Banner Data:", error);
            setBannerData([]);
            setFilteredBannerData([]);
        }
    };

    useEffect(() => {
        if (!Token) {
            console.warn("No token found. Please login first.");
            enqueueSnackbar("Please login first", { variant: "warning" });
            return;
        }
        getAllHeroSection();
        getAllBanners();
    }, [Token, enqueueSnackbar]);


    // Validate file type and size
    const validateImage = (file) => {
        const validTypes = ["image/jpeg", "image/jpg", "image/png"];
        const maxSize = 1024 * 1024; // 1 MB
        return validTypes.includes(file.type) && file.size <= maxSize;
    };

    // Combined handleImageChange: preview + base64
    const handleImageChange = async (e) => {
        const files = Array.from(e.target.files);
        const newImages = [];

        // ✅ Max 5 files total validation (existing + new)
        if (Images.length + files.length > 5) {
            enqueueSnackbar("You can select a maximum of 5 images in total.", { variant: "warning" });
            return;
        }

        for (const file of files) {
            if (validateImage(file)) {
                const previewUrl = URL.createObjectURL(file);
                const base64 = await fileToBase64Raw(file);

                newImages.push({
                    file,
                    preview: previewUrl,
                    name: file.name,
                    base64, // ✅ stored for backend upload
                });
            } else {
                enqueueSnackbar(`${file.name} is invalid (only JPG/PNG under 1 MB allowed)`, { variant: "warning" });
            }
        }

        setImages([...Images, ...newImages]);
    };



    const removeImage = (index) => {
        if (Images[index] && Images[index].preview && Images[index].preview.startsWith('blob:')) {
            URL.revokeObjectURL(Images[index].preview);
        }
        const updatedImages = Images.filter((_, i) => i !== index);
        setImages(updatedImages);
    };

    useEffect(() => {
        return () => {
            Images.forEach((image) => {
                if (image?.preview && image.preview.startsWith('blob:')) {
                    URL.revokeObjectURL(image.preview);
                }
            });
        };
    }, [Images]);

    const insertBanner = async () => {
        try {
            if (Images.length === 0) {
                enqueueSnackbar("Please upload at least one image", { variant: "warning" });
                setLoading(false);
                return;
            }

            // Extract base64 strings from Images state
            const base64Array = Images.map(img => img.base64);

            const payload = {
                Images: base64Array,        // ✅ must be array of strings
                HeroSectionId: HeroSectionId,
                IsActive: true,
                CreatedBy: UserId
            };

            const response = await fetch(APIUrl.BannerSection + 'UploadImages/', {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${Token}`
                },
                body: JSON.stringify(payload)
            });

            const result = await parseJSON(response);

            if (!result || !result.isSuccess || result.Message !== "Success") {
                enqueueSnackbar(result?.Message || "Failed to add banner", { variant: "error" });
                setLoading(false);
                return;
            }

            if (result.Message === "Success") {
                getAllBanners();
                doEmptyStates();
                enqueueSnackbar("Banner Successfully Added", { variant: "success" });
            }
            setLoading(false);

        } catch (error) {
            console.error("Error:", error);
            enqueueSnackbar("Error adding Banner", { variant: "error" });
        }
    };

    const fileToBase64Raw = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result;
                const base64String = result.replace(/^data:.+;base64,/, "");
                resolve(base64String);
            };
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
        });
    }; // end fileToBase64Raw

    const ValidateBannerAndSubmit = async () => {
        try {
            if (!HeroSectionId) {
                enqueueSnackbar("Please select a Hero Section", { variant: "warning" });
                HeroSectionRef.current.focus();
                return false;
            }

            if (Images.length === 0) {
                enqueueSnackbar("Please upload at least one image", { variant: "warning" });
                return false;
            }

            setLoading(true);
            await insertBanner();
        }
        catch (error) {
            console.log(error);
            setLoading(false);
        }
    };

    const UpdateBanner = async (Id) => {
        try {
            if (!HeroSectionId || !IndexNo) {
                enqueueSnackbar("Fill all required details to update", { variant: "error" });
                setLoading(false);
                return;
            }

            const formData = new FormData();
            formData.append('BannerId', Id);
            formData.append('HeroSectionId', HeroSectionId);
            formData.append('IndexNo', IndexNo);
            formData.append('IsActive', IsActive);
            formData.append('ModifyBy', UserId);

            const newImages = Images.filter((image) => image?.file);
            if (newImages.length > 0) {
                newImages.forEach((image) => {
                    formData.append('Image', image.file);
                });
            }

            const response = await fetch(APIUrl.BannerSection + 'UpdateBannerSection/' + Id, {
                method: "POST",
                headers: {
                    'Authorization': `Bearer ${Token}`
                },
                body: formData
            });

            const result = await parseJSON(response);

            if (result.isSuccess && result.Message === "Success") {
                getAllBanners();
                doEmptyStates();
                enqueueSnackbar("Banner Successfully Updated", { variant: "success" });
            } else {
                enqueueSnackbar(result.Message || "Failed to update banner", { variant: "error" });
            }
        } catch (error) {
            console.error("Error:", error);
            enqueueSnackbar("Error updating banner", { variant: "error" });
        }
        setLoading(false);
    };

    const activateDeactivateBanner = async (Id, _Value) => {
        try {
            let data = { BannerId: Id, Value: _Value };
            const response = await fetch(APIUrl.BannerSection + 'ActivateDeactivateBannerSection',
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${Token}`
                    },
                    body: JSON.stringify(data)
                });

            const result = await parseJSON(response);
            if (result.Message == "Success") {

                _Value === "activate" ?
                    enqueueSnackbar("Successfully Activated", { variant: "success" })
                    :
                    enqueueSnackbar("Successfully Deactivated", { variant: "success" });

                getAllBanners();
                doEmptyStates();
            }
            else {
                enqueueSnackbar("Failed to update status", { variant: "warning" });
            }
        }
        catch (error) {
            console.log(error);
            enqueueSnackbar("Error updating status", { variant: "error" });
        }
    };

    const UpdateIndexNo = async (Id) => {
        try {
            if (!IndexNo) {
                enqueueSnackbar("Please enter index no to update", { variant: "warning" });
                return;
            }
            let data = { BannerId: Id, IndexNo: IndexNo, ModifyBy: UserId };
            const response = await fetch(APIUrl.BannerSection + 'UpdateIndexofBannerinBannerSection',
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${Token}`
                    },
                    body: JSON.stringify(data)
                });

            const result = await parseJSON(response);
            if (result.Message == "Success") {
                enqueueSnackbar("Index No Successfully Updated", { variant: "success" });
                getAllBanners();
                doEmptyStates();
            }
            else {
                enqueueSnackbar("Failed to update Index No", { variant: "warning" });
            }
        }
        catch (error) {
            console.log(error);
            enqueueSnackbar("Error updating Index No", { variant: "error" });
        }
    };

    const editBanner = async (Id) => {
        try {
            let items = BannerData.filter(x => {
                return x.BannerId === Id;
            });

            if (items.length > 0) {
                const item = items[0];
                setBannerId(item.BannerId);
                setHeroSectionId(item.HeroSectionId);
                setIndexNo(item.IndexNo);
                setIsActive(item.IsActive);

                if (item.Image) {
                    const imagePath = item.Image.startsWith('http') ? item.Image : `${APIUrl.BaseUrl}${item.Image}`;
                    const imageName = item.Image.split('/').pop() || 'existing-image';
                    setImages([
                        {
                            file: null,
                            preview: imagePath,
                            name: imageName,
                            base64: null,
                            isExisting: true
                        }
                    ]);
                } else {
                    setImages([]);
                }
            }
            setEdit(true);
            if (HeroSectionRef.current) {
                HeroSectionRef.current.focus();
            }
        }
        catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        const result = BannerData.filter(x => {
            const heroTitle = HeroSectionList.find(h => h.HeroSectionId === x.HeroSectionId)?.Title || "";
            return heroTitle.toLowerCase().match(search.toLowerCase());
        });
        setFilteredBannerData(result);
    }, [search, BannerData, HeroSectionList]);

    const getHeroSectionTitle = (heroSectionId) => {
        const heroSection = HeroSectionList.find(h => h.HeroSectionId === heroSectionId);
        return heroSection ? heroSection.Title : "N/A";
    };
    const columns = [
        {
            name: "SNo.",
            selector: (_, index) => index + 1,
            width: "70px"
        },
        {
            name: "Image",
            cell: (row) => row.Image ?
                <div style={{ display: "flex", flexDirection: "column", gap: "4px", margin: "10px 0px 10px 0px" }}>
                    <span>Index No <span style={{ fontWeight: "bolder" }}>{row.IndexNo}</span></span>
                    <img src={APIUrl.BaseUrl + row.Image} alt="Banner" style={{ height: '100px', width: '100px', objectFit: 'contain' }} />
                    <span style={{ fontWeight: "bolder" }}>{row.Title}</span>
                </div>
                : null,
            width: "500px"
        },
        {
            name: "Update Index No.",
            cell: (row) => (
                <div className="flex flex-col items-center gap-2 p-2">
                    <input
                        type="number"
                        min="1"
                        max="5"
                        placeholder="Enter Index"
                        defaultValue={row.IndexNo}
                        onChange={(e) => setIndexNo(e.target.value)}
                        className="w-20 px-2 py-1 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <button
                        onClick={() => {
                            setLoading(true);
                            UpdateIndexNo(row.BannerId);
                            setLoading(false);
                        }}
                        disabled={loading}
                        className="px-3 py-1 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-colors duration-200"
                    >
                        {loading ? "..." : "Update"}
                    </button>
                </div>
            ),
            width: "200px"
        },
        {
            name: "Status",
            cell: (row) => row.IsActive ? <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Active</label> : <label className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Inactive</label>,
        },
        {
            name: "Action",
            cell: row =>
                <div>
                    {!row.IsActive
                        ?
                        <Button onClick={() => activateDeactivateBanner(row.BannerId, "activate")} className="text-white bg-gradient-to-r from-red-400 via-red-500 to-red-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-red-300 font-medium rounded-lg text-sm px-4 py-2"><i className="fa fa-toggle-off" aria-hidden="true"></i></Button>
                        :
                        <Button onClick={() => activateDeactivateBanner(row.BannerId, "deactivate")} className="text-white bg-gradient-to-r from-green-400 via-green-500 to-green-600 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-green-300 font-medium rounded-lg text-sm px-4 py-2"><i className="fa fa-toggle-on" aria-hidden="true"></i></Button>
                    }&nbsp;&nbsp;
                    {/* <Button onClick={() => editBanner(row.BannerId)} className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-4 py-2"><i className="fa fa-pencil" aria-hidden="true"></i></Button> */}
                </div>
        }
    ];

    return (<div>
        <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">
                    Banner Management
                </h2>

                <div>
                    <label htmlFor="herosection" className="block text-sm font-medium text-gray-700 mb-2">
                        Hero Section <span className="text-red-500">*</span>
                    </label>
                    <select
                        ref={HeroSectionRef}
                        value={HeroSectionId}
                        onChange={(e) => setHeroSectionId(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    >
                        <option value="">Select Hero Section</option>
                        {HeroSectionList.map((hero) => (
                            <option key={hero.HeroSectionId} value={hero.HeroSectionId}>
                                {hero.Title}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="mt-4">
                    <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-2">
                        Images <span className="text-red-500">*</span> (JPG, JPEG, PNG - Max 1 MB each, Multiple allowed)
                    </label>
                    <input
                        type="file"
                        ref={ImageRef}
                        accept=".jpg,.jpeg,.png"
                        onChange={handleImageChange}
                        multiple
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
                    />
                </div>

                {Images.length > 0 && (
                    <div className="mt-4">
                        <p className="text-sm font-medium text-gray-700 mb-3">Selected Images ({Images.length})</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                            {Images.map((image, index) => (
                                <div key={index} className="relative">
                                    <img
                                        src={image.preview}
                                        alt={`Preview ${index + 1}`}
                                        style={{ height: '150px', width: '100%', objectFit: 'cover' }}
                                        className="rounded-lg border border-gray-300"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                    >
                                        <i className="fa fa-times"></i>
                                    </button>
                                    <p className="text-xs text-gray-600 mt-1 truncate">{image.name}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className="flex flex-col sm:flex-row gap-4 mt-6">
                    <Button
                        disabled={loading}
                        className="px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-colors duration-200 flex items-center justify-center space-x-2"
                        onClick={() => {
                            if (edit) {
                                setLoading(true);
                                UpdateBanner(BannerId);
                            } else {
                                ValidateBannerAndSubmit();
                            }
                        }}
                    >
                        {loading
                            ? <><Spinner animation="border" size="sm" /> <span>{edit ? "Updating..." : "Submitting..."}</span></>
                            : <span>{edit ? "Update" : "Submit"}</span>}
                    </Button>
                    <button
                        type="button"
                        onClick={() => {
                            if (!loading) doEmptyStates();
                        }}
                        disabled={loading}
                        className="px-6 py-3 bg-gray-600 text-white font-medium rounded-lg hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors duration-200"
                    >
                        Reset
                    </button>

                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mt-6">
                <div className="overflow-x-auto">
                    <DataTable
                        title="Manage Banner Data"
                        columns={columns}
                        data={filteredBannerData}
                        pagination
                        fixedHeader
                        fixedHeaderScrollHeight="550px"
                        selectableRows
                        selectableRowsHighlight
                        highlightOnHover
                        subHeader
                        subHeaderAlign="right"
                        subHeaderComponent={
                            <input type="text"
                                placeholder="Search by hero section"
                                className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        }
                    />
                </div>
            </div>
        </div>
    </div>
    )
}


export default Banner;